export const AICAM_WARNING_MESSAGES = {
  "Low light": "Image is taken in a possibly low light environment",
  "Obstructed image": "There is possibly an obstruction in the image",
  "Sleet image": "Car is possibly covered with sleet",
  "Raindrops image": "Rain/Rain Droplets detected on car surface",
  "Blur image": "Image is possibly blurry",
  "Dirty image": "The system has detected that the image possibly has dirt",
  "Far image": "The vehicle seems far away",
  "Screen image": "Screen images detected",
};

export const formatSideName = (side) => {
  if (!side) return "";
  const withSpaces = side.toString().replace(/([a-z])([A-Z])/g, "$1 $2");
  const cleaned = withSpaces.replace(/_/g, " ");
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
};

const collectWarningMessages = (warningData) => {
  if (!warningData) return [];

  if (typeof warningData === "string") {
    return [warningData];
  }

  if (Array.isArray(warningData)) {
    return warningData.flatMap(collectWarningMessages);
  }

  if (typeof warningData !== "object") {
    return [];
  }

  const warningSource = warningData.warning || warningData;
  const message =
    warningSource.warning_message ||
    warningSource.warningMessage ||
    warningSource.message ||
    warningSource["warning message"];

  if (message) {
    return collectWarningMessages(message);
  }

  return Object.values(warningData).flatMap(collectWarningMessages);
};

export const buildWarningMessageList = (assessment) => {
  const allWarningList =
    assessment?.all_warning_list ||
    assessment?.info?.all_warning_list ||
    assessment?.data?.all_warning_list;

  return collectWarningMessages(allWarningList)
    .map((message) => message.trim())
    .filter(Boolean)
    .map((message) => ({ message }));
};

/**
 * Whether this case's own warnings say the user never granted location
 * permission — checked against *both* places that can say so, because
 * CaptureIssues (and the case list) only ever read one of them
 * (all_warning_list, via buildWarningMessageList) while this used to check
 * only the other (the structured `warnings` object). A case whose pipeline
 * warning list names it but whose `warnings.location_given` is absent/true
 * would otherwise show "User did not provide location permission" in
 * Capture Issues while Location Check stayed fully clickable right next to
 * it — the mismatch this exists to close.
 */
export const hasNoLocationPermissionWarning = (assessment) => {
  if (assessment?.warnings?.location_given === false) return true;
  return buildWarningMessageList(assessment).some((issue) =>
    /did not provide location permission/i.test(issue.message),
  );
};

export const buildCaptureWarnings = (warnings, baseUrl = "") => {
  const issues = [];
  const seen = new Set();

  const getImageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${baseUrl}${path.toString().startsWith("/") ? path : `/${path}`}`;
  };

  const addIssue = (issue) => {
    const key = JSON.stringify({
      message: issue.message,
      sidesData: Array.isArray(issue.sidesData) ? issue.sidesData : null,
    });

    if (seen.has(key)) return;
    seen.add(key);
    issues.push(issue);
  };

  if (!warnings || typeof warnings !== "object") return issues;

  if (warnings.vin_valid === false) {
    addIssue({ message: "The system couldn't read the VIN" });
  }

  if (warnings.num_plate_valid === false) {
    addIssue({ message: "Number plate not readable" });
  }

  if (warnings.location_changed) {
    addIssue({ message: "Location changed" });
  }

  if (warnings.location_given === false) {
    addIssue({ message: "User did not provide location permission" });
  }

  if (Array.isArray(warnings.partial_sides) && warnings.partial_sides.length) {
    const sides = [...new Set(warnings.partial_sides.map(formatSideName))];
    addIssue({
      message: `${sides.join(", ")} side image${
        sides.length > 1 ? "s are" : " is"
      } partial`,
      sidesData: null,
    });
  }

  if (
    warnings.incorrect_sides &&
    typeof warnings.incorrect_sides === "object"
  ) {
    const sidesData = Object.entries(warnings.incorrect_sides).map(
      ([path, side]) => ({
        name: formatSideName(side),
        imageUrl: getImageUrl(path),
      })
    );

    if (sidesData.length) {
      addIssue({ message: "Incorrect side captured: ", sidesData });
    }
  }

  if (warnings.non_car && typeof warnings.non_car === "object") {
    const sidesData = Object.entries(warnings.non_car).map(([path, side]) => ({
      name: formatSideName(side),
      imageUrl: getImageUrl(path),
    }));

    if (sidesData.length) {
      addIssue({
        message: "Image captured is not a passenger car: ",
        sidesData,
      });
    }
  }

  if (
    warnings.aicam_warnings &&
    typeof warnings.aicam_warnings === "object"
  ) {
    Object.entries(warnings.aicam_warnings).forEach(([path, warningType]) => {
      addIssue({
        message: AICAM_WARNING_MESSAGES[warningType] || "AI detected issue",
        sidesData: [{ name: warningType, imageUrl: getImageUrl(path) }],
      });
    });
  }

  return issues;
};
